import { useState, useEffect, useRef } from "react";
import { Lock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

export const PasswordDialog = ({ open, onOpenChange, onSuccess }) => {
  const [password, setPassword] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [turnstileToken, setTurnstileToken] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const turnstileContainerRef = useRef(null);
  const widgetIdRef = useRef(null);

  // Render Turnstile when dialog opens; reset/remove when it closes.
  useEffect(() => {
    if (!open || !TURNSTILE_SITE_KEY) return;

    let cancelled = false;
    const tryRender = () => {
      if (cancelled || widgetIdRef.current !== null) return;
      // Retry until BOTH the Turnstile script AND the dialog container are ready.
      // The script can be cached (window.turnstile already present) while the
      // dialog's portal content mounts a tick later — if we only waited on the
      // script, the widget would never render and the submit button would stay
      // permanently disabled.
      if (window.turnstile && turnstileContainerRef.current) {
        widgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: "auto",
          callback: (token) => {
            setTurnstileToken(token);
            setError("");
          },
          "error-callback": () => {
            setTurnstileToken("");
            setError("Captcha failed to load. Refresh the page and try again.");
          },
          "expired-callback": () => setTurnstileToken(""),
        });
      } else {
        setTimeout(tryRender, 150);
      }
    };
    tryRender();

    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current !== null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {}
        widgetIdRef.current = null;
      }
      setTurnstileToken("");
    };
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError("Please complete the captcha");
      return;
    }

    setLoading(true);
    const result = await login(password, { website, turnstileToken });
    setLoading(false);

    if (result.success) {
      setPassword("");
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } else {
      setError(result.error);
      // Reset captcha so user gets a fresh token on retry
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.reset(widgetIdRef.current);
        setTurnstileToken("");
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Admin Access Required
          </DialogTitle>
          <DialogDescription>
            Enter the password to add, edit, or delete recipes.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Honeypot — visually hidden but in the DOM. Bots fill it; real users don't. */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-9999px",
              top: "auto",
              width: 1,
              height: 1,
              overflow: "hidden",
            }}
          >
            <label htmlFor="website-field">Website</label>
            <input
              id="website-field"
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              autoFocus
              data-testid="admin-password-input"
            />
            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}
          </div>

          {TURNSTILE_SITE_KEY && (
            <div ref={turnstileContainerRef} className="flex justify-center" />
          )}

          <div className="flex gap-2">
            <Button
              type="submit"
              disabled={loading || !password || (TURNSTILE_SITE_KEY && !turnstileToken)}
              className="flex-1 rounded-full"
              data-testid="admin-password-submit"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Lock className="w-4 h-4 mr-2" />
              )}
              Unlock
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-full"
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
